import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, RefreshCw, FileJson, FileArchive, AlertTriangle, Mail, Calendar, CheckCircle2, XCircle, Send } from "lucide-react";
import { toast } from "sonner";

const DOMAIN_ICONS = {
  zeiterfassung: "⏱️",
  lenkprotokoll: "🛣️",
  fahrer: "👤",
  fahrzeuge: "🚚",
  karten: "💳",
  dokumente: "📄",
  finanzen: "💰",
  lohnabrechnung: "💶",
  konfiguration: "⚙️",
  audit: "📜",
  database: "🗄️",
};

const DOMAIN_DESCRIPTIONS = {
  zeiterfassung: "Zeiteinträge, Auto-Generator-Drafts, Überschreib-Verlauf, Feiertage.",
  lenkprotokoll: "Fahrtenbuch / Driving Logs + zugehörige Zeiteinträge.",
  fahrer: "Mitarbeiter-Stammdaten, Abwesenheiten, Schichten, Abteilungen.",
  fahrzeuge: "Fahrzeuge, Schäden, KFZ-Übergaben, Leasingverträge.",
  karten: "Tank- und Begehkarten (FuelCards).",
  dokumente: "Dokumente, Checklisten, Vorlagen.",
  finanzen: "Rechnungen, Zahlungen, Aufträge, Kunden, Boni.",
  lohnabrechnung: "Payroll-Läufe und -Regeln.",
  konfiguration: "Settings, Einsatzorte, Auto-Generator-Regeln.",
  audit: "Vollständiges Audit-Log (alle Aktionen).",
  database: "Komplettes Daten­bank-Backup über ALLE Kollektionen.",
};

export default function BackupCenter() {
  const [domains, setDomains] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // `${key}-${format}`
  const [schedule, setSchedule] = useState(null);
  const [sendingNow, setSendingNow] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [{ data: dd }, { data: sd }] = await Promise.all([
        api.get("/settings/backup/domains"),
        api.get("/settings/backup/email-schedule").catch(() => ({ data: null })),
      ]);
      setDomains(dd.domains || []);
      if (!active && dd.domains?.length) setActive(dd.domains[0].key);
      setSchedule(sd);
    } catch (e) {
      toast.error("Backup-Bereiche konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function sendNow() {
    setSendingNow(true);
    try {
      const { data } = await api.post("/settings/backup/email-now");
      if (data.status === "ok") {
        toast.success(`Backup per E-Mail an ${data.to} gesendet (${(data.size_bytes / 1024 / 1024).toFixed(1)} MB)`);
      } else if (data.status === "oversize") {
        toast.warning(`Backup zu groß für E-Mail (${(data.size_bytes / 1024 / 1024).toFixed(1)} MB). Bitte manuell herunterladen.`);
      } else if (data.error && /Resend.*nicht konfiguriert/i.test(data.error)) {
        toast.error("Resend API-Key fehlt — bitte unter Einstellungen → APIs hinterlegen", { duration: 6000 });
      } else {
        toast.error(`Versand fehlgeschlagen: ${data.error || data.status}`);
      }
      await load();
    } catch (e) {
      toast.error(`Versand fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSendingNow(false);
    }
  }

  async function downloadDomain(domain, fmt) {
    const key = `${domain}-${fmt}`;
    setDownloading(key);
    try {
      const res = await api.get(`/settings/backup/domain/${domain}.${fmt}`, {
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `munja_backup_${domain}.${fmt}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fname} heruntergeladen`);
    } catch (e) {
      toast.error(`Backup fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDownloading(null);
    }
  }

  const activeDomain = domains.find(d => d.key === active);

  return (
    <div className="space-y-4" data-testid="backup-center">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-zinc-100">Datensicherung (Backup)</h3>
            <p className="text-xs text-zinc-500 max-w-2xl mt-0.5">
              Laden Sie zielgerichtete Backups einzelner Datenbereiche oder der gesamten
              Datenbank herunter. Backups enthalten KEINE Passwörter (werden redigiert).
              Wählen Sie zwischen JSON (eine Datei) oder ZIP (JSON + CSV je Kollektion).
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="backup-refresh-btn"
          className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded text-xs text-zinc-200 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </button>
      </div>

      {/* Weekly email-backup schedule */}
      {schedule?.schedule && (
        <EmailScheduleCard
          schedule={schedule.schedule}
          recentRuns={schedule.recent_runs || []}
          sendingNow={sendingNow}
          onSendNow={sendNow}
        />
      )}

      {/* Sub-tabs by domain */}
      <div className="flex gap-1 flex-wrap border-b border-zinc-800 pb-1">
        {domains.map(d => (
          <button
            key={d.key}
            onClick={() => setActive(d.key)}
            data-testid={`backup-tab-${d.key}`}
            className={`px-3 py-2 text-xs rounded-t border-b-2 transition-colors inline-flex items-center gap-1.5 ${
              active === d.key
                ? "text-zinc-50 border-emerald-500 bg-zinc-900/50"
                : "text-zinc-400 border-transparent hover:text-zinc-200"
            }`}
          >
            <span className="text-sm leading-none">{DOMAIN_ICONS[d.key] || "📦"}</span>
            <span>{d.label}</span>
            <span className="ml-1 px-1.5 py-0.5 rounded bg-zinc-800/70 text-[10px] font-mono text-zinc-400">
              {d.total_documents.toLocaleString("de-AT")}
            </span>
          </button>
        ))}
      </div>

      {/* Active domain detail */}
      {loading && (
        <div className="text-sm text-zinc-500">Lade Backup-Bereiche…</div>
      )}

      {!loading && activeDomain && (
        <div className="border border-zinc-800 rounded-md bg-zinc-950/40 p-5 space-y-4" data-testid={`backup-panel-${activeDomain.key}`}>
          <div className="flex items-start gap-3">
            <div className="text-3xl leading-none" aria-hidden>{DOMAIN_ICONS[activeDomain.key] || "📦"}</div>
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h4 className="text-sm font-semibold text-zinc-100">{activeDomain.label}</h4>
                <div className="text-xs text-zinc-500">
                  Gesamt: <span className="font-mono text-zinc-300">{activeDomain.total_documents.toLocaleString("de-AT")}</span> Datensätze
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                {DOMAIN_DESCRIPTIONS[activeDomain.key] || ""}
              </p>
            </div>
          </div>

          {/* Collection breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {activeDomain.collections.map(cname => (
              <div key={cname} className="flex items-center justify-between px-2.5 py-1.5 bg-zinc-900/60 border border-zinc-800 rounded text-xs">
                <span className="text-zinc-400 font-mono">{cname}</span>
                <span className="font-mono text-zinc-300 tabular-nums">
                  {(activeDomain.counts[cname] ?? 0).toLocaleString("de-AT")}
                </span>
              </div>
            ))}
          </div>

          {/* Warning for full DB */}
          {activeDomain.key === "database" && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-500/5 border border-amber-500/30 rounded text-xs text-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                Komplettes Datenbank-Backup. Datei kann je nach Datenmenge mehrere
                hundert MB groß werden — Download kann etwas dauern.
              </div>
            </div>
          )}

          {/* Download actions */}
          <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-800">
            <button
              onClick={() => downloadDomain(activeDomain.key, "json")}
              disabled={downloading === `${activeDomain.key}-json`}
              data-testid={`backup-download-json-${activeDomain.key}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-900 disabled:cursor-wait text-white rounded text-sm font-medium transition-colors"
            >
              {downloading === `${activeDomain.key}-json`
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <FileJson className="w-4 h-4" />}
              JSON herunterladen
            </button>
            <button
              onClick={() => downloadDomain(activeDomain.key, "zip")}
              disabled={downloading === `${activeDomain.key}-zip`}
              data-testid={`backup-download-zip-${activeDomain.key}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 disabled:cursor-wait text-white rounded text-sm font-medium transition-colors"
            >
              {downloading === `${activeDomain.key}-zip`
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <FileArchive className="w-4 h-4" />}
              ZIP (JSON + CSV) herunterladen
            </button>
          </div>

          <div className="text-[11px] text-zinc-600 pt-1">
            Passwörter werden in allen Backups automatisch entfernt. Backups werden in
            Audit-Log dokumentiert.
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Weekly email-backup schedule card ─────────────────────────────────────
function EmailScheduleCard({ schedule, recentRuns, sendingNow, onSendNow }) {
  const dayLabels = {
    mon: "Montag", tue: "Dienstag", wed: "Mittwoch", thu: "Donnerstag",
    fri: "Freitag", sat: "Samstag", sun: "Sonntag",
  };
  const dayLabel = dayLabels[schedule.day_of_week] || schedule.day_of_week;
  const hh = String(schedule.hour).padStart(2, "0");
  const mm = String(schedule.minute).padStart(2, "0");

  function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-AT", {
        timeZone: "Europe/Vienna",
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  }
  function fmtMb(b) {
    if (!b) return "—";
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  }

  return (
    <div className="border border-blue-500/30 bg-blue-500/5 rounded-md p-5 space-y-4" data-testid="backup-email-schedule">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-zinc-100">Wöchentliches Backup per E-Mail</h4>
            {schedule.enabled ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-[10px] font-semibold text-emerald-300 uppercase tracking-wider">
                <CheckCircle2 className="w-3 h-3" /> Aktiv
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-700/40 border border-zinc-600/30 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                <XCircle className="w-3 h-3" /> Deaktiviert
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            Komplettes Datenbank-Backup wird automatisch als ZIP-Anhang per E-Mail versendet.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div className="px-3 py-2 bg-zinc-950/60 border border-zinc-800 rounded">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Empfänger</div>
          <div className="text-zinc-100 font-mono truncate" title={schedule.recipient} data-testid="backup-email-recipient">
            {schedule.recipient}
          </div>
        </div>
        <div className="px-3 py-2 bg-zinc-950/60 border border-zinc-800 rounded">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Zeitplan</div>
          <div className="text-zinc-100 inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-blue-400" />
            <span data-testid="backup-email-schedule-time">Jeden {dayLabel} · {hh}:{mm} Uhr</span>
          </div>
        </div>
        <div className="px-3 py-2 bg-zinc-950/60 border border-zinc-800 rounded">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Zeitzone</div>
          <div className="text-zinc-100 font-mono">{schedule.timezone}</div>
        </div>
        <div className="px-3 py-2 bg-zinc-950/60 border border-zinc-800 rounded">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Nächster Lauf</div>
          <div className="text-zinc-100 text-[11px]" data-testid="backup-email-next-run">
            {fmtDateTime(schedule.next_run_at)}
          </div>
        </div>
      </div>

      {/* Manual trigger */}
      <div className="space-y-2 pt-2 border-t border-zinc-800">
        {/* Inline hint when last attempt failed due to missing Resend key */}
        {recentRuns?.[0]?.error && /Resend.*nicht konfiguriert/i.test(recentRuns[0].error) && (
          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-500/5 border border-amber-500/30 rounded text-xs text-amber-200" data-testid="backup-email-resend-warning">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Resend API-Key fehlt.</strong> Backups werden gebaut, aber der
              Versand schlägt fehl. Bitte den Resend-Key unter <em>Einstellungen → APIs</em>
              hinterlegen, dann ist der automatische Wochenversand sofort aktiv.
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={onSendNow}
            disabled={sendingNow}
            data-testid="backup-email-send-now"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 disabled:cursor-wait text-white rounded text-sm font-medium transition-colors"
          >
            {sendingNow ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sendingNow ? "Backup wird gesendet…" : "Jetzt testen — Backup per E-Mail senden"}
          </button>
          <div className="text-[11px] text-zinc-500">
            Max. Anhang: {schedule.max_attachment_mb} MB · Bei größerer DB wird automatisch nur eine Benachrichtigung versendet.
          </div>
        </div>
      </div>

      {/* Recent runs */}
      {recentRuns.length > 0 && (
        <div className="pt-2 border-t border-zinc-800">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Letzte Versand-Versuche</div>
          <div className="space-y-1">
            {recentRuns.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-2.5 py-1.5 bg-zinc-950/40 border border-zinc-800/50 rounded text-xs" data-testid={`backup-email-run-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {r.status === "ok" ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  ) : r.status === "oversize" ? (
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                  )}
                  <span className="text-zinc-300 truncate">{fmtDateTime(r.started_at)}</span>
                  {r.filename && (
                    <span className="text-zinc-500 font-mono text-[10px] truncate">· {r.filename}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[10px] text-zinc-500 flex-shrink-0">
                  <span className="font-mono">{fmtMb(r.size_bytes)}</span>
                  {r.error && <span className="text-red-400 max-w-[180px] truncate" title={r.error}>{r.error}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
